import { Controller } from "@hotwired/stimulus"
import { lock, unlock } from 'tua-body-scroll-lock'

export default class extends Controller {
  static targets = ["form", "input", "submit", "content","scrollForMore"]

  static values = {
    open: { type: Boolean, default: false }
  }

  connect() {
    // console.log("ModalController connected", this.hasFormTarget)
    document.addEventListener('modal:show', this.show);
    document.addEventListener('modal:hide', this.hide);
    document.addEventListener('modal:submit', this.submit);
    // Bind once and keep the reference — this listener is on `window` (survives page
    // navigation), so disconnect() must remove exactly this function or it leaks.
    this.boundHandlePopstate = this.handlePopstate.bind(this)
    window.addEventListener('popstate', this.boundHandlePopstate);
    if (this.openValue) {
      this.show()
    }

    // Add MutationObserver to watch for content changes
    this.mutationObserver = new MutationObserver((mutations) => {
      this.checkForOverflow();
    });
  }

  disconnect() {
    document.removeEventListener('modal:show', this.show);
    document.removeEventListener('modal:hide', this.hide);
    window.removeEventListener('popstate', this.boundHandlePopstate);
    document.removeEventListener('modal:submit', this.submit);

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  handlePopstate(event) {
    this.hide(false);
  }

  contentTargetConnected(content) {
    // Configure and start the mutation observer
    this.mutationObserver.observe(this.contentTarget, {
      childList: true,     // Watch for changes in direct children
      subtree: true,       // Watch for changes in all descendants
      characterData: true  // Watch for changes in text content
    });
    
    // Add ResizeObserver to watch for content size changes
    this.resizeObserver = new ResizeObserver((entries) => {
      this.checkForOverflow();
    });

    this.resizeObserver.observe(this.contentTarget);
    
    // Check on initial load
    this.checkForOverflow();
    this.contentTarget.addEventListener("scroll", this.handleScroll.bind(this))
  }

  formTargetConnected(form) {
    // console.log("formTargetConnected", form)
    form.addEventListener("turbo:submit-end", this.submitEnd)
    form.addEventListener("submit", e => {
      /* This is to prevent the browser from focussing on the next form on the page if they hit enter */
      document.activeElement.blur()
      this.submitTargets.forEach((submit) => {
        submit.classList.add("loading")
      })
      form.requestSubmit()
    })
    form.focus()
  }

  formTargetDisconnected(form) {
    form.removeEventListener("turbo:submit-end", this.submitEnd)
  }

  inputTargetConnected(input) {
    input.addEventListener("change", this.fetchForm.bind(this));
    input.addEventListener("click", this.fetchForm.bind(this));
  }

  enableSubmit() {
    this.submitTargets.forEach((submit) => {
      submit.removeAttribute("disabled")
    })
  }

  submit(event) {
    // console.log("submit", event)

    if (event.params && event.params.submitType) {
      var submitType = document.createElement("input");
      submitType.setAttribute("type", "hidden");
      submitType.setAttribute("name", "submit_type");
      submitType.setAttribute("value", event.params.submitType);
      this.formTarget.appendChild(submitType);
    }

    event.preventDefault();
    // console.log("submit", this.hasFormTarget, this.formTarget)
    if (this.hasFormTarget) {
      document.activeElement.blur()
      this.submitTargets.forEach((submit) => {
        submit.classList.add("loading")
      })
      this.formTarget.requestSubmit()
    }
  }

  submitEnd = (e) => {
    const fetchResponse = e.detail.fetchResponse
    this.submitTargets.forEach((submit) => {
      submit.classList.remove("loading")
    })
    if(fetchResponse) {
      if(e.detail.success) {
        //if the response header contains a header called modal, then we will not hide the modal
        if(fetchResponse.response.headers.get("modal_redirect_to")) {
          // Turbo.visit(fetchResponse.headers.get("modal_redirect_to"))
          let frame = document.querySelector("turbo-frame#modal")
          frame.src = fetchResponse.response.headers.get("modal_redirect_to")
        } else {
          if(!fetchResponse.response.redirected) {
            document.dispatchEvent(new Event('modal:hide')); // hide the modal via event to allow for other controllers to be notified 
          }
        }
      }
    }
  }

  show = (event) => {
    // document.getElementById("modal").innerHTML = ""
    this.element.classList.add("active")
    if (event && event.detail?.modal) {
      this.element.classList.add(event.detail.modal)
    }
    history.pushState({ modalOpen: true }, '', '');
    lock(document.body)
    document.dispatchEvent(new Event('document:lock'));
  }

  hide = (enableHistory=true) => {
    if (this.element.classList.contains("active")) {
      this.element.classList.remove("blur");
      this.element.classList.remove("active");
      if (enableHistory) {
        // This causes issues when performing a search/filter change
        // When the modal closes it will retrigger the search, which we don't want.
        // It looks like I can safely disable this, as everything seems to work still, 
        // but leaving it in here for now, as presumably there was a use for it.
        //   history.back()
      }

      this.removeModalParamFromUrl()

      setTimeout(() => {
        unlock(document.body);
        document.dispatchEvent(new Event('document:unlock'));
        document.getElementById("modal").innerHTML = "";
        document.getElementById("modal-side").innerHTML = "";
        this.element.classList.remove("large");
      }, 500)
    }
  }

  removeModalParamFromUrl() {
    // Remove only the modal parameter from the URL, keeping other parameters
    // the modal parameter will be used to trigger a modal popup (i.e. mentions inbox)
    if (window.location.search.includes("modal")) {
      setTimeout(() => {
        const params = new URLSearchParams(window.location.search);
        params.delete("modal");
        window.history.replaceState({}, document.title, `${location.pathname}?${params}`);
      }, 0)
    } 
  }

  /* Fetch form */
  async fetchForm() {
    const response = await fetch(this.urlWithQueryString(), {
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
      }
    });
    const html = await response.text();
    Turbo.renderStreamMessage(html);
  }

  urlWithQueryString() {
    return `${document.getElementById('modal').getAttribute('src')}?${this.queryString()}`;
  }

  url() {
    return window.location.href;
  }

  queryString() {
    const form = new FormData(this.formTarget);
    const params = new URLSearchParams();
    for (const [name, value] of form.entries()) {
      params.append(name, value);
    }

    return params.toString();
  }

  side = () => {
    // If the modal has a submit button, we disable auto hide, but otherwise we allow it to hide
    const submit = this.element.querySelector(".modal-actions [type='submit']")
    if (submit) {
      return false
    }
    this.hide();
  }

  checkForOverflow() {
    if (this.hasContentTarget) {
      if (this.contentTarget.scrollHeight > this.contentTarget.clientHeight) {
        this.scrollForMoreTarget.classList.add("active")
        this.handleScroll({target: this.contentTarget}) 
      } else {
        this.scrollForMoreTarget.classList.remove("active")
      }
    }
  }

  handleScroll (event) {
    const target = event.target
    if (target) {
      const scrollable = target.scrollHeight - target.clientHeight

      if ((target.scrollTop >= (scrollable - 100))) {
        this.scrollForMoreTarget.classList.remove('active')
      } else {
        this.scrollForMoreTarget.classList.add('active')
      }
    }
  }

  scrollToBottom(event) {
    event.stopPropagation()
    this.contentTarget.scrollTo({
      top: this.contentTarget.scrollHeight,
      behavior: 'smooth'
    })
  }

}